import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from '../../../../../config/axios'

import {
  Container,
  HeaderDiv,
  HeaderText,
  CloseDiv,
  ContentContainer,
  DetailGrid,
  DetailItem,
  DetailLabel,
  DetailValue,
  MaintenanceSectionHeader,
  ButtonContainer,
  EditButton
} from './style'

import { BlurBackground } from '../../style'

import cross from '../../../../../assets/addStockModal/cross.svg'
import editIcon from '../../../../../assets/edit-pen-white.svg'

interface ViewVehicleModalProps {
  vehicleId: string
  onClose: () => void
}

interface BackendVehicleDetail {
  id: string
  plate: string
  model: string
  year: number
  make: string
  bodyType: string
  capacity: number
  capacityUnit: string
  restriction: string | null
  periodicMaintenances: Array<{
    id?: number
    maintenanceName: string
    periodicityValue: number
    periodicityUnit: string
  }>
}

interface VehicleDetailItem {
  label: string
  value: string
}

interface PeriodicVehicleMaintenanceItem {
  name: string
  periodicity: string
}

interface FullVehicleData {
  headerDisplay: string
  general: VehicleDetailItem[]
  maintenance: PeriodicVehicleMaintenanceItem[]
}

const API_BASE_URL = `${process.env.REACT_APP_GATEWAY_BASE_URL}/equipment`

const ViewVehicleModal: React.FC<ViewVehicleModalProps> = ({
  vehicleId,
  onClose,
}) => {
  const navigate = useNavigate()

  const [vehicleData, setVehicleData] = useState<FullVehicleData | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  const formatcapacity = useCallback(
    (value: number | undefined | null, unit: string | undefined): string => {
      if (value === null || value === undefined || isNaN(value)) {
        return 'N/A'
      }
      return `${value.toLocaleString('pt-BR')}${unit ? unit : ''}`
    },
    [],
  )

  useEffect(() => {
    const fetchVehicleData = async () => {
      if (!vehicleId) {
        setLoading(false)
        return
      }

      setLoading(true)
      setError(null)

      const token = localStorage.getItem('token')
      if (!token) {
        setError('Sessão expirada. Faça login novamente.')
        setLoading(false)
        navigate('/login')
        return
      }

      try {
        const response = await axios.get<BackendVehicleDetail>(
          `${API_BASE_URL}/vehicles/${vehicleId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        )
        const backendData = response.data

        const mappedHeaderDisplay = `${backendData.model || 'N/A'} ${
          backendData.plate || 'N/A'
        }`

        const mappedDetails: VehicleDetailItem[] = [
          { label: 'Placa', value: backendData.plate || 'N/A' },
          { label: 'Modelo', value: backendData.model || 'N/A' },
          { label: 'Ano', value: backendData.year?.toString() || 'N/A' },
          {
            label: 'Capacidade de carga',
            value: formatcapacity(
              backendData.capacity,
              backendData.capacityUnit,
            ),
          },
          {
            label: 'Tamanho da carroceria',
            value: backendData.bodyType || 'N/A',
          },
          {
            label: 'Restrição de rodagem',
            value: backendData.restriction || 'N/A',
          },
        ]

        const mappedMaintenance: PeriodicVehicleMaintenanceItem[] = (
          backendData.periodicMaintenances || []
        ).map((item) => ({
          name: item.maintenanceName || 'N/A',
          periodicity: `A cada ${item.periodicityValue.toLocaleString(
            'pt-BR',
          )}${item.periodicityUnit || ''}`,
        }))

        setVehicleData({
          headerDisplay: mappedHeaderDisplay,
          general: mappedDetails,
          maintenance: mappedMaintenance,
        })
      } catch (err) {
        console.error('Erro ao buscar dados do veículo:', err)
        if (axios.isAxiosError(err) && err.response?.status === 401) {
          setError('Não autorizado. Por favor, faça login novamente.')
          setLoading(false)
          navigate('/login')
        } else if (axios.isAxiosError(err) && err.response?.status === 404) {
          setError('Veículo não encontrado.')
          setLoading(false)
        } else {
          setError(
            'Falha ao carregar os dados do veículo. Por favor, tente novamente.',
          )
          setLoading(false)
        }
      } finally {
        setLoading(false)
      }
    }

    fetchVehicleData()
  }, [vehicleId, navigate, formatcapacity])

  const handleClose = () => {
    onClose()
  }

  const handleEditVehicle = () => {
    navigate(`/panel/vehicles/edit/${vehicleId}`)
    onClose()
  }

  if (loading) {
    return (
      <BlurBackground> 
        <Container>
          <HeaderDiv>
            <HeaderText>Carregando dados do veículo...</HeaderText>
            <CloseDiv onClick={handleClose}>
              <img src={cross} alt='Fechar' />
            </CloseDiv>
          </HeaderDiv>
          <ContentContainer>
            <p>Carregando informações do veículo...</p>
          </ContentContainer>
        </Container>
      </BlurBackground>
    )
  }

  if (error) {
    return (
      <BlurBackground> 
        <Container>
          <HeaderDiv>
            <HeaderText>Erro ao carregar dados</HeaderText>
            <CloseDiv onClick={handleClose}>
              <img src={cross} alt='Fechar' />
            </CloseDiv>
          </HeaderDiv>
          <ContentContainer>
            <p style={{ color: 'red' }}>{error}</p>
          </ContentContainer>
        </Container>
      </BlurBackground>
    )
  }

  if (!vehicleData) {
    return (
      <BlurBackground>
        <Container>
          <HeaderDiv>
            <HeaderText>Veículo não encontrado</HeaderText>
            <CloseDiv onClick={handleClose}>
              <img src={cross} alt='Fechar' />
            </CloseDiv>
          </HeaderDiv>
          <ContentContainer>
            <p>Nenhum dado encontrado para o veículo com ID: {vehicleId}.</p>
          </ContentContainer>
        </Container>
      </BlurBackground>
    )
  }

  return (
    <BlurBackground>
      <Container>
        <HeaderDiv>
          <HeaderText>{vehicleData.headerDisplay}</HeaderText>
          <CloseDiv onClick={handleClose}>
            <img src={cross} alt='Fechar' />
          </CloseDiv>
        </HeaderDiv>

        <ContentContainer>
          <DetailGrid>
            {vehicleData.general.map((item, index) => (
              <DetailItem key={index}>
                <DetailLabel>{item.label}</DetailLabel>
                <DetailValue>{item.value}</DetailValue>
              </DetailItem>
            ))}

            <MaintenanceSectionHeader>
              Manutenção periódica
            </MaintenanceSectionHeader>

            {vehicleData.maintenance.length > 0 ? (
              vehicleData.maintenance.map((item, index) => (
                <DetailItem
                  key={`maintenance-${index}`}
                  style={{ gridColumn: '1 / -1' }}
                >
                  <DetailLabel>{item.name}</DetailLabel>
                  <DetailValue>{item.periodicity}</DetailValue>
                </DetailItem>
              ))
            ) : (
              <DetailItem style={{ gridColumn: '1 / -1' }}>
                <DetailLabel>Nenhuma manutenção</DetailLabel>
                <DetailValue>
                  Nenhuma manutenção periódica registrada.
                </DetailValue>
              </DetailItem>
            )}
          </DetailGrid>
        </ContentContainer>

        <ButtonContainer>
          <EditButton onClick={handleEditVehicle}>
            <img src={editIcon} alt='Editar' />
            Editar veículo
          </EditButton>
        </ButtonContainer>
      </Container>
    </BlurBackground>
  )
}

export default ViewVehicleModal

